// ===========================================================================================================================================
// - Tab-scoped persistence (namespaced snapshot, debounce) — CreateTenderPage-style
// - Uses Tender DTO (Tender returns Markets/TransportTypes/Carriers/FCL/LCL/Phases)
// - UX hydrate snapshot with useLayoutEffect so content renders with data on first paint
// - Inline views (no heavyweight parents being mounted/unmounted)
// - Persistence hardening:
//     1) Namespaced Redux snapshot per tab (ex: "<tabId>::TFIN").
//     2) SessionStorage mirror per namespace (survives route unmounts / slice resets inside the session).
//     3) Aggressive flush on: tab switch, component unmount, and beforeunload (no data lost on navigation).
//     4) Debounced flush for normal state churn (reduce Redux noise).
// - Single source of truth for the page header label, tender data and view selection
// - API update:
//     1) Quotes are now loaded from a single endpoint: TenderQuotes (filter by TenderId + IsAccepted).
//     2) Lanes are now loaded from TenderLanes (filter by TenderId).
//     3) Distinct carriers come from TenderQuotes/distinctCarriers.
//     4) Lane metrics now need to support ALL comparison types:
//        - Best Unit Value
//        - Bid Value
//        - Loads
//        - ETD
//     5) IMPORTANT EXCEPTION:
//        - CarrierByLaneTable still renders and compares BidValue, not UnitPrice.
//        - Therefore this page now builds TWO best-metric maps:
//            a) laneBestMap         -> richer map with UnitPrice + BidValue + Loads + ETD, used by ViewLaneTable
//            b) carrierLaneBestMap  -> BidValue-based compatibility map, used ONLY by CarrierByLaneTable
//     6) FUTURE PREPARATION:
//        - ViewLaneTable will later show "Loads Remaining To Satisfy".
//        - This page already computes and passes:
//            a) tenderTargetLoads
//            b) laneRemainingLoadsByLaneId
// -----------------------------------------------------------------------------------------------------------------------------------------------------------

import React, {
  useEffect,
  useLayoutEffect,
  useState,
  useContext,
  useMemo,
  useRef,
  useCallback,
} from "react";
import { useSelector, useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";
import {
  LoadingContext,
  MHeader,
  MButton,
  BufferService,
  M_StateActions,
  NavigateContext,
  MSelect,
} from "src/M_Components";
import PropTypes from "prop-types";
import { isFailurePayload, apiMessage } from "src/components/utils/apiResponse";

import TopsByCarrierTable from "../../components/Tenders/TopsByCarrierTable";
import TopsByDestinationTable from "../../components/Tenders/TopsByDestinationTable";
import TopsBySourceTable from "../../components/Tenders/TopsBySourceTable";
import ViewLaneTable from "../../components/Tenders/ViewLaneTable";
import CarrierByLaneTable from "../../components/Tenders/carrierByLaneTable";
import TenderSummaryTable from "../../components/Tenders/TenderSummaryTable";

import "./TenderFinishedPage.css";

//region Helpers (pure + tiny)

/**
 * Stable namespace for this page.
 *
 * @param {string|number|null|undefined} tid - Tab id.
 * @returns {string|null} Namespace key or null.
 */
const stateKeyFrom = (tid) => (tid ? `${tid}::TFIN` : null);

/**
 * Picks a value from a snapshot array of shape [{ key, value }].
 *
 * @param {Array<{key:string,value:any}>} arr - Snapshot array.
 * @param {string} k - Key to read.
 * @returns {any} Stored value or undefined.
 */
const pickKV = (arr, k) => (arr || []).find((x) => x.key === k)?.value;

/**
 * Normalizes common API array shapes.
 *
 * @param {any} data - API payload.
 * @returns {Array} Normalized array.
 */
const norm = (data) =>
  Array.isArray(data?.Items)
    ? data.Items
    : Array.isArray(data)
      ? data
      : data || [];

/**
 * Cheap deep equality used for snapshot persistence.
 *
 * @param {any} a - First value.
 * @param {any} b - Second value.
 * @returns {boolean} True when equal enough.
 */
const deepEq = (a, b) => {
  try {
    return JSON.stringify(a) === JSON.stringify(b);
  } catch {
    return a === b;
  }
};

/**
 * Normalizes any numeric-like value.
 *
 * @param {any} value - Raw value.
 * @returns {number|null} Finite number or null.
 */
const toFiniteNumber = (value) => {
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
};

/**
 * SessionStorage key builder for snapshot mirrors.
 *
 * @param {string} ns - Namespace key.
 * @returns {string} Storage key.
 */
const ssKey = (ns) => `NS_SNAPSHOT:${ns}`;

/**
 * Reads one namespace snapshot from sessionStorage.
 *
 * @param {string|null} ns - Namespace key.
 * @returns {Array|null} Parsed snapshot or null.
 */
const readFromStorage = (ns) => {
  if (!ns) return null;

  try {
    const raw = sessionStorage.getItem(ssKey(ns));
    if (!raw) return null;

    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : null;
  } catch {
    return null;
  }
};

/**
 * Writes a namespace snapshot to sessionStorage.
 *
 * @param {string|null} ns - Namespace key.
 * @param {Array} snapshotArray - Snapshot entries.
 * @returns {void}
 */
const writeToStorage = (ns, snapshotArray) => {
  if (!ns) return;

  try {
    sessionStorage.setItem(
      ssKey(ns),
      JSON.stringify(Array.isArray(snapshotArray) ? snapshotArray : [])
    );
  } catch {
    // Storage may be unavailable/full. Redux snapshot remains the main source of truth.
  }
};

/**
 * Safely reads UnitPrice from a quote.
 *
 * @param {object} quote - TenderQuote row.
 * @returns {number|null} UnitPrice or null.
 */
const getQuoteUnitPrice = (quote) =>
  toFiniteNumber(quote?.UnitPrice ?? quote?.unitPrice);

/**
 * Safely reads BidValue from a quote.
 *
 * @param {object} quote - TenderQuote row.
 * @returns {number|null} BidValue or null.
 */
const getQuoteBidValue = (quote) =>
  toFiniteNumber(quote?.BidValue ?? quote?.bidValue);

/**
 * Safely reads Loads from a quote.
 *
 * @param {object} quote - TenderQuote row.
 * @returns {number|null} Loads or null.
 */
const getQuoteLoads = (quote) => toFiniteNumber(quote?.Loads ?? quote?.loads);

/**
 * Safely reads EstimatedTransitDays from a quote.
 *
 * @param {object} quote - TenderQuote row.
 * @returns {number|null} ETD or null.
 */
const getQuoteEstimatedTransitDays = (quote) =>
  toFiniteNumber(
    quote?.EstimatedTransitDays ?? quote?.estimatedTransitDays
  );

/**
 * Safely reads LaneId from a quote.
 *
 * @param {object} quote - TenderQuote row.
 * @returns {number|null} Lane id or null.
 */
const getQuoteLaneId = (quote) =>
  toFiniteNumber(quote?.LaneId ?? quote?.laneId);

/**
 * Safely reads LaneId from a lane row.
 *
 * @param {object} lane - TenderLane row.
 * @returns {number|null} Lane id or null.
 */
const getLaneId = (lane) =>
  toFiniteNumber(lane?.LaneId ?? lane?.laneId ?? lane?.Id ?? lane?.id);

/**
 * Safely resolves whether a quote row is active.
 *
 * @param {object} quote - TenderQuote row.
 * @returns {boolean} Active flag.
 */
const getQuoteIsActive = (quote) => {
  if (!quote) return true;

  const inactive = quote.Inactive ?? quote.inactive;
  if (inactive !== undefined) return !inactive;

  return true;
};

/**
 * Safely resolves whether a lane row is active.
 *
 * @param {object} lane - TenderLane row.
 * @returns {boolean} Active flag.
 */
const getLaneIsActive = (lane) => {
  if (!lane) return true;

  if (lane.IsActive !== undefined) return !!lane.IsActive;
  if (lane.isActive !== undefined) return !!lane.isActive;

  const inactive = lane.Inactive ?? lane.inactive;
  if (inactive !== undefined) return !inactive;

  return true;
};

/**
 * Normalizes carriers to a stable internal shape.
 *
 * @param {Array} carriers - Raw carrier rows.
 * @returns {Array<{id:number|string,name:string}>} Normalized carriers.
 */
const normalizeCarriers = (carriers) =>
  (carriers || []).map((c) => ({
    id: c.CarrierId ?? c.Id,
    name: c.CarrierName ?? c.Name ?? "",
  }));

/**
 * Builds lane lookup maps once.
 *
 * @param {Array} lanes - Lane rows.
 * @returns {{laneIdToDestination:Object,laneIdToSource:Object}} Lookup maps.
 */
const buildLaneMaps = (lanes) => {
  const laneIdToDestination = {};
  const laneIdToSource = {};

  (lanes || []).forEach((l) => {
    laneIdToDestination[l.LaneId] = l.LaneDestination;
    laneIdToSource[l.LaneId] = l.LaneSource;
  });

  return { laneIdToDestination, laneIdToSource };
};

/**
 * Generic group by helper.
 *
 * @param {Array} quotes - Rows to group.
 * @param {Function} keyFn - Key selector.
 * @returns {Map<any, Array>} Grouped rows.
 */
const groupQuotesBy = (quotes, keyFn) => {
  const map = new Map();

  (quotes || []).forEach((q) => {
    const key = keyFn(q);
    if (key == null) return;

    const arr = map.get(key) || [];
    arr.push(q);
    map.set(key, arr);
  });

  return map;
};

/**
 * Computes the best (top1) and N-th best DISTINCT UnitPrice in a single pass.
 *
 * Important:
 * - the default dashboard remains UnitPrice-based;
 * - lower UnitPrice is better;
 * - this stays O(n) per group and avoids sorting the full array.
 *
 * @param {Array} quotesArr - Quote rows for the current group.
 * @param {number} nth - Desired N-th distinct best value.
 * @returns {{top1:number|null,topN:number|null}} Best values.
 */
const topFromQuotes = (quotesArr, nth) => {
  if (!Array.isArray(quotesArr) || quotesArr.length === 0) {
    return { top1: null, topN: null };
  }

  let first = Number.POSITIVE_INFINITY;
  let second = Number.POSITIVE_INFINITY;

  for (const q of quotesArr) {
    const v = getQuoteUnitPrice(q);
    if (!Number.isFinite(v)) continue;

    if (v < first) {
      if (first !== Number.POSITIVE_INFINITY && v !== first) {
        second = first;
      }
      first = v;
    } else if (v > first && v < second) {
      second = v;
    }
  }

  if (!Number.isFinite(first)) {
    return { top1: null, topN: null };
  }

  const top1 = first;

  let topN = top1;
  if (nth >= 2 && Number.isFinite(second)) {
    topN = second;
  }

  return { top1, topN };
};

/**
 * Builds the finished-tender dashboard model used by the default view tables.
 *
 * Important:
 * - all "top" commercial values here refer to UnitPrice;
 * - the downstream dashboard components can keep their structure unchanged.
 *
 * @param {Array} quotes - Accepted quotes.
 * @param {Array} lanes - Tender lanes.
 * @param {Array} carriers - Distinct carriers.
 * @param {number} nth - Requested N-th rank.
 * @returns {object} Aggregated dashboard model.
 */
const buildFinishedTenderModel = (quotes, lanes, carriers, nth = 2) => {
  const normCarriers = normalizeCarriers(carriers);
  const carrierMap = new Map(normCarriers.map((c) => [c.id, c]));
  const { laneIdToDestination, laneIdToSource } = buildLaneMaps(lanes);

  const quotesByLane = groupQuotesBy(quotes, (q) => q.LaneId);
  const quotesByDestination = groupQuotesBy(
    quotes,
    (q) => laneIdToDestination[q.LaneId]
  );
  const quotesBySource = groupQuotesBy(quotes, (q) => laneIdToSource[q.LaneId]);

  const topByLane = new Map();
  quotesByLane.forEach((arr, laneId) =>
    topByLane.set(laneId, topFromQuotes(arr, nth))
  );

  const topByDestination = new Map();
  quotesByDestination.forEach((arr, dest) =>
    topByDestination.set(dest, topFromQuotes(arr, nth))
  );

  const topBySource = new Map();
  quotesBySource.forEach((arr, src) =>
    topBySource.set(src, topFromQuotes(arr, nth))
  );

  return {
    nth,
    normCarriers,
    carrierMap,
    laneIdToDestination,
    laneIdToSource,
    quotesByLane,
    quotesByDestination,
    quotesBySource,
    topByLane,
    topByDestination,
    topBySource,
  };
};

/**
 * Builds lightweight accepted-quote metrics by lane.
 *
 * Why this exists:
 * - the backend summary endpoint is still the preferred source for lane metrics;
 * - but for BidValue support and future "loads remaining" work we want a safe
 *   fallback computed from the already loaded accepted quotes;
 * - this does not add expensive FE processing because the quote set is already
 *   loaded for the page.
 *
 * Output per lane:
 * - minUnitPrice
 * - minBidValue
 * - maxLoads
 * - minEstimatedTransitDays
 * - totalLoads
 *
 * @param {Array} quotes - Accepted quote rows.
 * @returns {Map<number, object>} Lane metrics map.
 */
const buildAcceptedQuoteMetricsByLane = (quotes) => {
  const map = new Map();

  for (const quote of Array.isArray(quotes) ? quotes : []) {
    if (!getQuoteIsActive(quote)) continue;

    const laneId = getQuoteLaneId(quote);
    if (!Number.isFinite(laneId)) continue;

    const unitPrice = getQuoteUnitPrice(quote);
    const bidValue = getQuoteBidValue(quote);
    const loads = getQuoteLoads(quote);
    const etd = getQuoteEstimatedTransitDays(quote);

    const current = map.get(laneId) || {
      minUnitPrice: null,
      minBidValue: null,
      maxLoads: null,
      minEstimatedTransitDays: null,
      totalLoads: 0,
      hasLoads: false,
    };

    if (unitPrice != null) {
      current.minUnitPrice =
        current.minUnitPrice == null
          ? unitPrice
          : Math.min(current.minUnitPrice, unitPrice);
    }

    if (bidValue != null) {
      current.minBidValue =
        current.minBidValue == null
          ? bidValue
          : Math.min(current.minBidValue, bidValue);
    }

    if (loads != null) {
      current.maxLoads =
        current.maxLoads == null ? loads : Math.max(current.maxLoads, loads);
      current.totalLoads += loads;
      current.hasLoads = true;
    }

    if (etd != null) {
      current.minEstimatedTransitDays =
        current.minEstimatedTransitDays == null
          ? etd
          : Math.min(current.minEstimatedTransitDays, etd);
    }

    map.set(laneId, current);
  }

  for (const [laneId, metrics] of map.entries()) {
    if (!metrics.hasLoads) {
      metrics.totalLoads = null;
    }
    delete metrics.hasLoads;
    map.set(laneId, metrics);
  }

  return map;
};

//endregion

//region Summary rows builder (pure)

/**
 * Builds TenderSummaryTable rows from:
 * - all lanes
 * - lane summaries endpoint
 *
 * Important:
 * - TenderSummaryTable expects maxUnitPrice/minUnitPrice fields;
 * - we also keep maxBidValue/minBidValue as compatibility aliases pointing to the same Unit values;
 * - TotalBidValue remains informational and continues to come from lane snapshot / backend aggregate.
 *
 * @param {object} args - Builder arguments.
 * @param {Array} args.allLanes - All tender lanes.
 * @param {Array} args.laneSummaries - Accepted lane summary rows.
 * @param {boolean} args.isSpotTender - Spot flag.
 * @returns {Array} Summary rows ready for TenderSummaryTable.
 */
const buildSummaryLaneRows = ({ allLanes, laneSummaries, isSpotTender }) => {
  const lanesArr = Array.isArray(allLanes) ? allLanes : [];
  const summariesArr = Array.isArray(laneSummaries) ? laneSummaries : [];

  const laneIdOf = (l) => l?.LaneId ?? l?.laneId ?? l?.Id ?? l?.id ?? null;
  const laneOriginOf = (l) =>
    l?.LaneSource ??
    l?.laneSource ??
    l?.SourceLocation ??
    l?.sourceLocation ??
    "";
  const laneDestinationOf = (l) =>
    l?.LaneDestination ??
    l?.laneDestination ??
    l?.DestinationLocation ??
    l?.destinationLocation ??
    "";

  const laneById = new Map(
    lanesArr.map((l) => [laneIdOf(l), l]).filter(([id]) => id != null)
  );

  const summaryByLaneId = new Map(
    summariesArr
      .map((s) => [s?.LaneId ?? s?.laneId ?? null, s])
      .filter(([id]) => id != null)
  );

  const rows = [];

  laneById.forEach((lane, laneId) => {
    const origin = laneOriginOf(lane);
    const destination = laneDestinationOf(lane);
    const summary = summaryByLaneId.get(laneId) || {};

    const totalQuotes = summary.TotalQuotes ?? summary.totalQuotes ?? 0;

    /**
     * Important:
     * - commercial max/min now come from UnitPrice fields;
     * - old BidValue fields are used only as fallback compatibility aliases.
     */
    const maxUnitPrice =
      summary.MaxUnitPrice ??
      summary.maxUnitPrice ??
      summary.MaxBidValue ??
      summary.maxBidValue ??
      null;

    const minUnitPrice =
      summary.MinUnitPrice ??
      summary.minUnitPrice ??
      summary.MinBidValue ??
      summary.minBidValue ??
      null;

    const totalLoads = summary.TotalLoads ?? summary.totalLoads ?? null;
    const maxLoads = summary.MaxLoads ?? summary.maxLoads ?? null;
    const minLoads = summary.MinLoads ?? summary.minLoads ?? null;

    const maxEstimatedTransitDays =
      summary.MaxEstimatedTransitDays ??
      summary.maxEstimatedTransitDays ??
      null;

    const minEstimatedTransitDays =
      summary.MinEstimatedTransitDays ??
      summary.minEstimatedTransitDays ??
      null;

    /**
     * Important:
     * - TotalBidValue stays informational and comes from the lane snapshot / backend aggregate;
     * - we do not sum quotes in the FE.
     */
    const totalBidValue =
      lane?.TotalBidValue ??
      lane?.totalBidValue ??
      summary.TotalBidValue ??
      summary.totalBidValue ??
      null;

    const isSatisfied =
      lane?.IsSatisfied ??
      lane?.isSatisfied ??
      lane?.Satisfied ??
      lane?.satisfied ??
      null;

    const satisfiedAt = lane?.SatisfiedAt ?? lane?.satisfiedAt ?? null;

    const satisfiedInPhase =
      lane?.SatisfiedInPhase ??
      lane?.satisfiedInPhase ??
      lane?.SatisfiedInTenderPhase ??
      lane?.satisfiedInTenderPhase ??
      null;

    rows.push({
      laneId,
      origin,
      destination,
      totalQuotes,
      totalBidValue,

      /**
       * Canonical fields expected by TenderSummaryTable.
       */
      maxUnitPrice,
      minUnitPrice,

      /**
       * Compatibility aliases for any older consumers still reading bid keys.
       * These now intentionally mirror Unit values.
       */
      maxBidValue: maxUnitPrice,
      minBidValue: minUnitPrice,

      totalLoads,
      maxLoads,
      minLoads,
      maxEstimatedTransitDays,
      minEstimatedTransitDays,
      isSatisfied,
      satisfiedAt,
      satisfiedInPhase: isSpotTender ? null : satisfiedInPhase,
    });
  });

  rows.sort((a, b) => {
    const ao = String(a.origin || "").localeCompare(String(b.origin || ""));
    if (ao !== 0) return ao;

    return String(a.destination || "").localeCompare(
      String(b.destination || "")
    );
  });

  return rows;
};

//endregion

//region Snapshot keys

/**
 * Persisted keys for this page namespace.
 */
const K = {
  owner: "TFIN.ownerTabId",
  operation: "TFIN.operation",
  sessionLine: "TFIN.sessionLine",
  activeView: "TFIN.activeView",
  tenderInfo: "TFIN.tenderInfo",
  quotes: "TFIN.quotes",
  lanes: "TFIN.lanes",
  laneSummaries: "TFIN.laneSummaries",
  carriers: "TFIN.carriers",
  metricKey: "TFIN.metricKey",
};

//endregion

//region Component

export default function TenderFinishedPage({ handleTabClose, toastMessages }) {
  const { t } = useTranslation();
  const ctx = useContext(LoadingContext);
  const dispatch = useDispatch();
  const { renameTab } = useContext(NavigateContext);

  //region Redux and tab state

  const selectedTab = useSelector(
    (s) => s.StateKeyValue.find((o) => o.key === "selectedTab")?.value
  );

  const rawTabsList = useSelector(
    (s) => s.StateKeyValue.find((o) => o.key === "tabs")?.value
  );

  const tabsList = useMemo(() => rawTabsList || [], [rawTabsList]);

  const globalOperation = useSelector(
    (s) => s.StateKeyValue.find((o) => o.key === "operation")?.value
  );

  const globalLine = useSelector(
    (s) => s.StateKeyValue.find((o) => o.key === "TenderLine")?.value
  );

  const stateKeyValue = useSelector((s) => s.StateKeyValue);

  const allStateKeys = useMemo(
    () => (stateKeyValue || []).map((o) => o.key),
    [stateKeyValue]
  );

  const currentNsKey = stateKeyFrom(selectedTab);

  const currentNsSnapshot = useMemo(() => {
    if (!currentNsKey) return [];

    const raw = stateKeyValue?.find((o) => o.key === currentNsKey)?.value;
    return Array.isArray(raw) ? raw : [];
  }, [stateKeyValue, currentNsKey]);

  //endregion

  //region Local UI state

  const [id, setId] = useState(selectedTab);

  useEffect(() => {
    setId(selectedTab);
  }, [selectedTab]);

  const [operation, setOperation] = useState(globalOperation ?? "View");
  const [sessionLine, setSessionLine] = useState(globalLine ?? null);

  /**
   * Inline views:
   * - default
   * - summary
   * - lanes
   * - carriers
   */
  const [activeView, setActiveView] = useState("default");
  const [isFirst, setIsFirst] = useState(true);

  const [tenderInfo, setTenderInfo] = useState({});
  const [quotes, setQuotes] = useState([]);
  const [lanes, setLanes] = useState([]);
  const [laneSummaries, setLaneSummaries] = useState([]);
  const [carriers, setCarriers] = useState([]);

  /**
   * Shared internal metric state.
   *
   * Supported values now:
   * - unit
   * - bid
   * - loads
   * - transit
   *
   * Important:
   * - lanes view supports all four;
   * - carriers view intentionally excludes "unit".
   */
  const [metricKey, setMetricKey] = useState("unit");

  /**
   * Metric selector options for the lanes view.
   *
   * Here all four best metrics are available:
   * - Best Unit Value
   * - Bid Value
   * - Loads
   * - ETD
   */
  const laneMetricOptions = useMemo(
    () => [
      { value: "unit", label: t("BidUnitValue") || "Bid Unit Value" },
      { value: "bid", label: t("BidValue") || "Bid Value" },
      { value: "loads", label: t("Loads") || "Loads" },
      {
        value: "transit",
        label: t("EstimatedTransitDays") || "Estimated Transit Days",
      },
    ],
    [t]
  );

  /**
   * Metric selector options for the carriers view.
   *
   * IMPORTANT:
   * - CarrierByLaneTable does not render UnitPrice;
   * - therefore "Best Unit Value" must NOT appear in this selector.
   */
  const carrierMetricOptions = useMemo(
    () => [
      { value: "bid", label: t("BidValue") || "Bid Value" },
      { value: "loads", label: t("Loads") || "Loads" },
      {
        value: "transit",
        label: t("EstimatedTransitDays") || "Estimated Transit Days",
      },
    ],
    [t]
  );

  const laneMetricSelectOptions = useMemo(
    () =>
      laneMetricOptions.map((o) => ({
        Id: o.value,
        value: o.value,
        label: o.label,
      })),
    [laneMetricOptions]
  );

  const carrierMetricSelectOptions = useMemo(
    () =>
      carrierMetricOptions.map((o) => ({
        Id: o.value,
        value: o.value,
        label: o.label,
      })),
    [carrierMetricOptions]
  );

  /**
   * Carrier view must never stay on "unit".
   *
   * Why:
   * - the user explicitly requested that Best Unit Value does not exist in Carrier;
   * - if we navigate from lanes -> carriers while "unit" is selected,
   *   we automatically fall back to "bid".
   */
  useEffect(() => {
    if (activeView === "carriers" && metricKey === "unit") {
      setMetricKey("bid");
    }
  }, [activeView, metricKey]);

  //endregion

  //region Services

  const baseUrl = ctx.DEFAULTURL;

  const tenderService = useMemo(
    () => new BufferService("Tenders", baseUrl),
    [baseUrl]
  );

  const tenderQuotesService = useMemo(
    () => new BufferService("TenderQuotes", baseUrl),
    [baseUrl]
  );

  const tenderLanesService = useMemo(
    () => new BufferService("TenderLanes", baseUrl),
    [baseUrl]
  );

  const tenderDistinctCarriersService = useMemo(
    () => new BufferService("TenderQuotes/distinctCarriers", baseUrl),
    [baseUrl]
  );

  const tenderLaneSummariesService = useMemo(
    () => new BufferService("TenderQuotes/laneSummaries/accepted", baseUrl),
    [baseUrl]
  );

  //endregion

  //region Snapshot infra

  const activeNsRef = useRef(null);
  const activeTidRef = useRef(null);
  const prevTabRef = useRef(null);
  const persistDebounceRef = useRef(null);
  const didGCRef = useRef(false);
  const hydratedOnceRef = useRef(false);
  const nsSnapshotRef = useRef([]);
  const rehydratingRef = useRef(false);

  useEffect(() => {
    if (currentNsKey && activeNsRef.current === currentNsKey) {
      nsSnapshotRef.current = Array.isArray(currentNsSnapshot)
        ? currentNsSnapshot
        : [];
    }
  }, [currentNsKey, currentNsSnapshot]);

  /**
   * Writes partial entries into the active namespace.
   *
   * @param {string|null} nsKey - Namespace key.
   * @param {Array<{key:string,value:any}>} entries - Entries to merge.
   * @returns {void}
   */
  const setSession = useCallback(
    (nsKey, entries) => {
      if (!nsKey) return;

      const current =
        nsKey === activeNsRef.current ? nsSnapshotRef.current || [] : [];

      const map = new Map(current.map((e) => [e.key, e]));
      let changed = false;

      for (const kv of entries) {
        const prevVal = map.get(kv.key)?.value;
        if (!deepEq(prevVal, kv.value)) {
          changed = true;
          map.set(kv.key, kv);
        }
      }

      if (changed) {
        const nextArray = Array.from(map.values());
        dispatch(M_StateActions.addM_State({ key: nsKey, value: nextArray }));
        writeToStorage(nsKey, nextArray);

        if (nsKey === activeNsRef.current) {
          nsSnapshotRef.current = nextArray;
        }
      }
    },
    [dispatch]
  );

  /**
   * Flushes the full page snapshot for one tab namespace.
   *
   * @param {string|number|null} forTabId - Optional explicit tab id.
   * @returns {void}
   */
  const flushSnapshot = useCallback(
    (forTabId = null) => {
      const tid = forTabId ?? activeTidRef.current ?? id;
      const ns = stateKeyFrom(tid);
      if (!ns) return;

      const payload = [
        { key: K.owner, value: String(tid) },
        { key: K.operation, value: operation },
        { key: K.sessionLine, value: sessionLine },
        { key: K.activeView, value: activeView },
        { key: K.tenderInfo, value: tenderInfo || {} },
        { key: K.quotes, value: quotes || [] },
        { key: K.lanes, value: lanes || [] },
        { key: K.laneSummaries, value: laneSummaries || [] },
        { key: K.carriers, value: carriers || [] },
        { key: K.metricKey, value: metricKey },
      ];

      setSession(ns, payload);
    },
    [
      id,
      operation,
      sessionLine,
      activeView,
      tenderInfo,
      quotes,
      lanes,
      laneSummaries,
      carriers,
      metricKey,
      setSession,
    ]
  );

  /**
   * Garbage-collects snapshots for closed tabs.
   */
  useEffect(() => {
    if (didGCRef.current) return;
    if (!tabsList || tabsList.length === 0) return;

    didGCRef.current = true;

    try {
      const openIds = new Set((tabsList || []).map((t) => String(t.value)));

      (allStateKeys || [])
        .filter((k) => typeof k === "string" && k.endsWith("::TFIN"))
        .forEach((ns) => {
          const tid = ns.split("::")[0];

          if (!openIds.has(String(tid))) {
            dispatch(M_StateActions.removeM_State({ key: ns }));
            sessionStorage.removeItem(ssKey(ns));
          }
        });
    } catch {
      // No action required if cleanup fails.
    }
  }, [tabsList, allStateKeys, dispatch]);

  /**
   * Hydrates the page snapshot before first paint.
   */
  useLayoutEffect(() => {
    const prev = prevTabRef.current;
    if (prev !== null && prev !== selectedTab) {
      flushSnapshot(prev);
    }

    if (!selectedTab) {
      prevTabRef.current = null;
      return;
    }

    const ns = stateKeyFrom(selectedTab);
    activeNsRef.current = ns;
    activeTidRef.current = selectedTab;

    let snap = Array.isArray(currentNsSnapshot) ? currentNsSnapshot : [];

    if (!snap.length) {
      const fromStore = readFromStorage(ns);
      if (Array.isArray(fromStore) && fromStore.length) {
        snap = fromStore;
        dispatch(M_StateActions.addM_State({ key: ns, value: snap }));
      }
    }

    rehydratingRef.current = true;

    const storedOwner = pickKV(snap, K.owner);
    const storedOp = pickKV(snap, K.operation);
    const storedLine = pickKV(snap, K.sessionLine);
    const storedView = pickKV(snap, K.activeView);
    const storedMetricKey = pickKV(snap, K.metricKey);

    const snapTender = pickKV(snap, K.tenderInfo);
    const snapQuotes = pickKV(snap, K.quotes);
    const snapLanes = pickKV(snap, K.lanes);
    const snapLaneSummaries = pickKV(snap, K.laneSummaries);
    const snapCarriers = pickKV(snap, K.carriers);

    if (storedOwner) {
      if (storedOp !== undefined) setOperation(storedOp);
      if (storedLine !== undefined) setSessionLine(storedLine);
      if (storedView !== undefined) setActiveView(storedView);
      if (storedMetricKey !== undefined) setMetricKey(storedMetricKey);

      if (snapTender) setTenderInfo(snapTender);
      if (Array.isArray(snapQuotes)) setQuotes(snapQuotes);
      if (Array.isArray(snapLanes)) setLanes(snapLanes);
      if (Array.isArray(snapLaneSummaries)) setLaneSummaries(snapLaneSummaries);
      if (Array.isArray(snapCarriers)) setCarriers(snapCarriers);

      hydratedOnceRef.current = true;
      nsSnapshotRef.current = snap;
    } else {
      const seed = [
        { key: K.owner, value: String(selectedTab) },
        { key: K.operation, value: operation },
        { key: K.sessionLine, value: sessionLine },
        { key: K.activeView, value: activeView },
        { key: K.tenderInfo, value: {} },
        { key: K.quotes, value: [] },
        { key: K.lanes, value: [] },
        { key: K.laneSummaries, value: [] },
        { key: K.carriers, value: [] },
        { key: K.metricKey, value: metricKey },
      ];

      dispatch(M_StateActions.addM_State({ key: ns, value: seed }));
      writeToStorage(ns, seed);
      nsSnapshotRef.current = seed;
      hydratedOnceRef.current = false;
    }

    setTimeout(() => {
      rehydratingRef.current = false;
    }, 0);

    prevTabRef.current = selectedTab || null;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTab]);

  /**
   * Flush on unload / unmount.
   */
  useEffect(() => {
    const handleBeforeUnload = () => {
      if (persistDebounceRef.current) clearTimeout(persistDebounceRef.current);
      flushSnapshot();
    };

    window.addEventListener("beforeunload", handleBeforeUnload);

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);

      if (persistDebounceRef.current) clearTimeout(persistDebounceRef.current);
      flushSnapshot();
    };
  }, [flushSnapshot]);

  /**
   * Debounced persistence for normal state churn.
   */
  useEffect(() => {
    if (!activeNsRef.current || rehydratingRef.current) return;

    if (persistDebounceRef.current) clearTimeout(persistDebounceRef.current);
    persistDebounceRef.current = setTimeout(() => flushSnapshot(), 200);

    return () => {
      if (persistDebounceRef.current) clearTimeout(persistDebounceRef.current);
    };
  }, [
    operation,
    sessionLine,
    activeView,
    tenderInfo,
    quotes,
    lanes,
    laneSummaries,
    carriers,
    metricKey,
    flushSnapshot,
  ]);

  /**
   * First mount seeding + cleanup of transient global keys.
   */
  useEffect(() => {
    if (!isFirst) return;

    setIsFirst(false);

    dispatch(M_StateActions.removeM_State({ key: "operation" }));
    dispatch(M_StateActions.removeM_State({ key: "TenderLine" }));

    const ns = stateKeyFrom(id);
    if (ns) {
      const seed = [
        { key: K.owner, value: String(id) },
        { key: K.operation, value: operation },
        { key: K.sessionLine, value: sessionLine },
        { key: K.activeView, value: activeView },
        { key: K.tenderInfo, value: tenderInfo },
        { key: K.quotes, value: quotes },
        { key: K.lanes, value: lanes },
        { key: K.laneSummaries, value: laneSummaries },
        { key: K.carriers, value: carriers },
        { key: K.metricKey, value: metricKey },
      ];

      dispatch(M_StateActions.addM_State({ key: ns, value: seed }));
      writeToStorage(ns, seed);
      nsSnapshotRef.current = seed;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isFirst]);

  //endregion

  //region Tab title

  useEffect(() => {
    if (!id || !renameTab) return;

    const base = t("TenderFinished");
    const name = tenderInfo?.Name || "";
    const label = name ? `${base} — ${name}` : base;

    renameTab(String(id), label);
  }, [id, tenderInfo?.Name, renameTab, t]);

  //endregion

  //region Loading refs

  const setIsLoadRef = useRef(ctx.setIsLoad);

  useEffect(() => {
    setIsLoadRef.current = ctx.setIsLoad;
  }, [ctx.setIsLoad]);

  //endregion

  //region Data loaders

  /**
   * Loads all accepted active quotes for the tender.
   *
   * Important:
   * - UnitPrice is normalized explicitly because it is now required by:
   *     - default dashboard
   *     - lanes best-unit comparison
   * - BidValue is ALSO normalized because:
   *     - lanes view now supports explicit BidValue comparison
   *     - carriers view still needs BidValue as its commercial metric
   */
  const loadAcceptedQuotesPaged = useCallback(
    async ({ tenderId }) => {
      if (!tenderId) {
        setQuotes([]);
        return [];
      }

      const pageSize = 200;
      let page = 1;
      const acc = [];
      const seen = new Set();

      const baseFilter = `TenderId eq ${tenderId} and IsAccepted eq true and Inactive eq false`;
      const qFilter = encodeURIComponent(baseFilter);

      while (true) {
        const res = await tenderQuotesService.filter(
          `?Page=${page}&PageSize=${pageSize}&filter=${qFilter}`
        );

        if (isFailurePayload(res)) break;

        const items = norm(res);
        if (!items.length) break;

        for (const it of items) {
          const rowId = it?.Id ?? it?.id;
          if (rowId != null && !seen.has(rowId)) {
            seen.add(rowId);
            acc.push(it);
          }
        }

        const totalPages = Number(
          res?.TotalPages ?? (items.length < pageSize ? page : page + 1)
        );

        if (page >= totalPages) break;
        page += 1;
      }

      const normalized = acc.map((q) => ({
        ...q,
        Id: q.Id ?? q.id,
        TenderId: q.TenderId ?? q.tenderId,
        TenderPhaseId: q.TenderPhaseId ?? q.tenderPhaseId,
        LaneId: q.LaneId ?? q.laneId,
        CarrierId: q.CarrierId ?? q.carrierId,
        UnitPrice: q.UnitPrice ?? q.unitPrice ?? null,
        BidValue: q.BidValue ?? q.bidValue ?? null,
        Loads: q.Loads ?? q.loads,
        EstimatedTransitDays: q.EstimatedTransitDays ?? q.estimatedTransitDays,
        Inactive: q.Inactive ?? q.inactive,
        IsAccepted: q.IsAccepted ?? q.isAccepted,
        AcceptedAt: q.AcceptedAt ?? q.acceptedAt,
        Carrier: q.Carrier ?? q.carrier,
        SourceLocation: q.SourceLocation ?? q.sourceLocation,
        DestinationLocation: q.DestinationLocation ?? q.destinationLocation,
      }));

      setQuotes(normalized);
      return normalized;
    },
    [tenderQuotesService]
  );

  /**
   * Loads all active tender lanes.
   */
  const loadTenderLanesPaged = useCallback(
    async ({ tenderId }) => {
      if (!tenderId) {
        setLanes([]);
        return [];
      }

      const pageSize = 200;
      let page = 1;
      const acc = [];
      const seen = new Set();

      const filter = encodeURIComponent(
        `TenderId eq ${tenderId} and Inactive eq false`
      );

      while (true) {
        const res = await tenderLanesService.filter(
          `?Page=${page}&PageSize=${pageSize}&filter=${filter}`
        );

        if (isFailurePayload(res)) break;

        const items = norm(res);
        if (!items.length) break;

        for (const it of items) {
          const rowId = it?.Id ?? it?.LaneId ?? it?.laneId;
          if (rowId != null && !seen.has(rowId)) {
            seen.add(rowId);
            acc.push(it);
          }
        }

        const totalPages = Number(
          res?.TotalPages ?? (items.length < pageSize ? page : page + 1)
        );

        if (page >= totalPages) break;
        page += 1;
      }

      const normalized = acc.map((l) => {
        const normalizedLane = {
          ...l,
          Id: l.Id ?? l.id,
          LaneId: l.LaneId ?? l.laneId ?? l.Id ?? l.id,
          LaneSource:
            l.LaneSource ??
            l.laneSource ??
            l.SourceLocation ??
            l.sourceLocation,
          LaneDestination:
            l.LaneDestination ??
            l.laneDestination ??
            l.DestinationLocation ??
            l.destinationLocation,
          Inactive: l.Inactive ?? l.inactive,
        };

        return {
          ...normalizedLane,
          IsActive: getLaneIsActive(normalizedLane),
        };
      });

      setLanes(normalized);
      return normalized;
    },
    [tenderLanesService]
  );

  /**
   * Loads distinct carriers already resolved by the backend.
   */
  const loadDistinctCarriersPaged = useCallback(
    async ({ tenderId }) => {
      if (!tenderId) {
        setCarriers([]);
        return [];
      }

      const pageSize = 200;
      let page = 1;
      const acc = [];
      const seen = new Set();

      const filter = encodeURIComponent(`TenderId eq ${tenderId}`);

      while (true) {
        const res = await tenderDistinctCarriersService.filter(
          `?Page=${page}&PageSize=${pageSize}&Filter=${filter}`
        );

        if (isFailurePayload(res)) break;

        const items = norm(res);
        if (!items.length) break;

        for (const it of items) {
          const carrier = it?.Carrier ?? it?.carrier ?? it;
          const carrierId = carrier?.Id ?? it?.CarrierId ?? it?.Id ?? it?.id;

          if (carrierId != null && !seen.has(carrierId)) {
            seen.add(carrierId);

            acc.push({
              CarrierId: carrierId,
              CarrierName: carrier?.Name ?? it?.CarrierName ?? it?.Name ?? "",
              Name: carrier?.Name ?? it?.CarrierName ?? it?.Name ?? "",
              QuotesCount: it?.QuotesCount ?? it?.quotesCount ?? null,
              QuotesAcceptedCount:
                it?.QuotesAcceptedCount ?? it?.quotesAcceptedCount ?? null,
            });
          }
        }

        const totalPages = Number(
          res?.TotalItems && pageSize
            ? Math.ceil(Number(res.TotalItems) / pageSize)
            : res?.TotalPages ?? (items.length < pageSize ? page : page + 1)
        );

        if (page >= totalPages) break;
        page += 1;
      }

      setCarriers(acc);
      return acc;
    },
    [tenderDistinctCarriersService]
  );

  /**
   * Loads accepted lane summaries.
   *
   * Important:
   * - UnitPrice metrics are normalized from the summary endpoint;
   * - BidValue metrics are kept ONLY when they really exist in the payload;
   * - we do NOT alias BidValue to UnitPrice here anymore, because lanes view now
   *   needs both metrics separately;
   * - when the backend payload does not include BidValue summary fields, we later
   *   fall back to a lightweight computation from already loaded accepted quotes.
   */
  const loadLaneSummariesPaged = useCallback(
    async ({ tenderId }) => {
      if (!tenderId) {
        setLaneSummaries([]);
        return [];
      }

      const pageSize = 200;
      let page = 1;
      const acc = [];
      const seen = new Set();

      const filter = encodeURIComponent(`TenderId eq ${tenderId}`);

      while (true) {
        const res = await tenderLaneSummariesService.filter(
          `?Page=${page}&PageSize=${pageSize}&Filter=${filter}`
        );

        if (isFailurePayload(res)) break;

        const items = norm(res);
        if (!items.length) break;

        for (const it of items) {
          const key = `${it?.TenderId ?? it?.tenderId ?? ""}::${it?.LaneId ?? it?.laneId ?? ""}`;

          if (!seen.has(key)) {
            seen.add(key);

            acc.push({
              ...it,
              TenderId: it?.TenderId ?? it?.tenderId,
              TenderPhaseId: it?.TenderPhaseId ?? it?.tenderPhaseId ?? null,
              LaneId: it?.LaneId ?? it?.laneId,
              TotalQuotes: it?.TotalQuotes ?? it?.totalQuotes ?? null,
              MaxUnitPrice: it?.MaxUnitPrice ?? it?.maxUnitPrice ?? null,
              MinUnitPrice: it?.MinUnitPrice ?? it?.minUnitPrice ?? null,
              MaxBidValue: it?.MaxBidValue ?? it?.maxBidValue ?? null,
              MinBidValue: it?.MinBidValue ?? it?.minBidValue ?? null,
              TotalLoads: it?.TotalLoads ?? it?.totalLoads ?? null,
              MaxLoads: it?.MaxLoads ?? it?.maxLoads ?? null,
              MinLoads: it?.MinLoads ?? it?.minLoads ?? null,
              MaxEstimatedTransitDays:
                it?.MaxEstimatedTransitDays ??
                it?.maxEstimatedTransitDays ??
                null,
              MinEstimatedTransitDays:
                it?.MinEstimatedTransitDays ??
                it?.minEstimatedTransitDays ??
                null,
            });
          }
        }

        const totalPages = Number(
          res?.TotalItems && pageSize
            ? Math.ceil(Number(res.TotalItems) / pageSize)
            : res?.TotalPages ?? (items.length < pageSize ? page : page + 1)
        );

        if (page >= totalPages) break;
        page += 1;
      }

      setLaneSummaries(acc);
      return acc;
    },
    [tenderLaneSummariesService]
  );

  //endregion

  //region Refresh orchestration

  /**
   * Reloads the full page data from the backend.
   */
  const onRefresh = useCallback(async () => {
    if (!sessionLine?.Id) return;

    setIsLoadRef.current?.(true);

    try {
      const tender = await tenderService.getId(sessionLine.Id);

      if (isFailurePayload(tender)) {
        toastMessages?.("error", "", apiMessage(tender, t("ErrorLoadingData")));
        setIsLoadRef.current?.(false);
        return;
      }

      setTenderInfo(tender);

      const lanesPromise = loadTenderLanesPaged({ tenderId: sessionLine.Id });
      const quotesPromise = loadAcceptedQuotesPaged({
        tenderId: sessionLine.Id,
      });
      const laneSummariesPromise = loadLaneSummariesPaged({
        tenderId: sessionLine.Id,
      });
      const carriersPromise = loadDistinctCarriersPaged({
        tenderId: sessionLine.Id,
      });

      await Promise.allSettled([
        lanesPromise,
        quotesPromise,
        laneSummariesPromise,
        carriersPromise,
      ]);

      hydratedOnceRef.current = true;
    } catch (err) {
      toastMessages?.("error", "", apiMessage(err, t("ErrorLoadingData")));
    } finally {
      setIsLoadRef.current?.(false);
    }
  }, [
    sessionLine?.Id,
    tenderService,
    loadTenderLanesPaged,
    loadAcceptedQuotesPaged,
    loadLaneSummariesPaged,
    loadDistinctCarriersPaged,
    toastMessages,
    t,
  ]);

  useEffect(() => {
    if (!sessionLine?.Id) return;
    onRefresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionLine?.Id]);

  //endregion

  //region Hydrated fallbacks

  const hvTender = useMemo(
    () =>
      Object.keys(tenderInfo || {}).length
        ? tenderInfo
        : pickKV(currentNsSnapshot, K.tenderInfo) || {},
    [tenderInfo, currentNsSnapshot]
  );

  const hvQuotes = useMemo(
    () =>
      quotes && quotes.length
        ? quotes
        : pickKV(currentNsSnapshot, K.quotes) || [],
    [quotes, currentNsSnapshot]
  );

  const hvLanes = useMemo(
    () =>
      lanes && lanes.length ? lanes : pickKV(currentNsSnapshot, K.lanes) || [],
    [lanes, currentNsSnapshot]
  );

  const hvCarriers = useMemo(
    () =>
      carriers && carriers.length
        ? carriers
        : pickKV(currentNsSnapshot, K.carriers) || [],
    [carriers, currentNsSnapshot]
  );

  const hvLaneSummaries = useMemo(
    () =>
      laneSummaries && laneSummaries.length
        ? laneSummaries
        : pickKV(currentNsSnapshot, K.laneSummaries) || [],
    [laneSummaries, currentNsSnapshot]
  );

  const hvAllLanes = useMemo(() => hvLanes || [], [hvLanes]);

  //endregion

  //region Precomputed lane/quote metric helpers

  /**
   * Lightweight quote-derived lane metrics.
   *
   * This is used as:
   * - a fallback for BidValue when the summary endpoint does not provide it;
   * - the source for the carrier-only best map;
   * - the source for future "loads remaining" fallback logic.
   */
  const acceptedQuoteMetricsByLane = useMemo(
    () => buildAcceptedQuoteMetricsByLane(hvQuotes),
    [hvQuotes]
  );

  /**
   * Fast summary lookup by lane id.
   */
  const laneSummaryByLaneId = useMemo(() => {
    const map = new Map();

    for (const row of hvLaneSummaries || []) {
      const laneId = toFiniteNumber(row?.LaneId ?? row?.laneId);
      if (!Number.isFinite(laneId)) continue;
      map.set(laneId, row);
    }

    return map;
  }, [hvLaneSummaries]);

  /**
   * Tender target loads.
   *
   * This is prepared now so ViewLaneTable can later show:
   * "Loads Remaining To Satisfy" = Tender.Loads - LaneTotalLoads.
   */
  const tenderTargetLoads = useMemo(
    () => toFiniteNumber(hvTender?.Loads ?? hvTender?.loads) ?? 0,
    [hvTender]
  );

  /**
   * Future-ready map for lane residual loads.
   *
   * Rule:
   * - residual = Tender.Loads - summary.TotalLoads for that lane
   * - when summary.TotalLoads is missing, we fall back to the accepted-quote-derived total
   * - we clamp to zero to avoid negative values in the UI
   */
  const laneRemainingLoadsByLaneId = useMemo(() => {
    const map = new Map();
    const laneIds = new Set();

    for (const lane of hvAllLanes || []) {
      const laneId = getLaneId(lane);
      if (Number.isFinite(laneId)) laneIds.add(laneId);
    }

    for (const row of hvLaneSummaries || []) {
      const laneId = toFiniteNumber(row?.LaneId ?? row?.laneId);
      if (Number.isFinite(laneId)) laneIds.add(laneId);
    }

    for (const laneId of laneIds) {
      const summary = laneSummaryByLaneId.get(laneId) || null;
      const quoteMetrics = acceptedQuoteMetricsByLane.get(laneId) || null;

      const totalLoads =
        toFiniteNumber(summary?.TotalLoads ?? summary?.totalLoads) ??
        quoteMetrics?.totalLoads ??
        0;

      map.set(laneId, Math.max(0, tenderTargetLoads - totalLoads));
    }

    return map;
  }, [
    hvAllLanes,
    hvLaneSummaries,
    laneSummaryByLaneId,
    acceptedQuoteMetricsByLane,
    tenderTargetLoads,
  ]);

  //endregion

  //region Lane best maps

  /**
   * Rich lane best map for ViewLaneTable.
   *
   * This map now supports ALL lane comparison metrics:
   * - bestUnitPrice / bestBidUnitValue
   * - bestBidValue
   * - bestLoadsValue
   * - bestEtdValue
   *
   * Priority:
   * 1) backend summary row
   * 2) lightweight quote-derived fallback
   *
   * Important:
   * - unlike the old implementation, bestBidValue is now a REAL BidValue baseline;
   * - it is no longer an alias for UnitPrice.
   */
  const laneBestMap = useMemo(() => {
    const map = new Map();
    const laneIds = new Set();

    for (const row of hvLaneSummaries || []) {
      const laneId = toFiniteNumber(row?.LaneId ?? row?.laneId);
      if (Number.isFinite(laneId)) laneIds.add(laneId);
    }

    for (const quote of hvQuotes || []) {
      const laneId = getQuoteLaneId(quote);
      if (Number.isFinite(laneId)) laneIds.add(laneId);
    }

    for (const laneId of laneIds) {
      const summary = laneSummaryByLaneId.get(laneId) || null;
      const quoteMetrics = acceptedQuoteMetricsByLane.get(laneId) || null;

      const bestUnitPrice =
        toFiniteNumber(summary?.MinUnitPrice ?? summary?.minUnitPrice) ??
        quoteMetrics?.minUnitPrice ??
        null;

      const bestBidValue =
        toFiniteNumber(summary?.MinBidValue ?? summary?.minBidValue) ??
        quoteMetrics?.minBidValue ??
        null;

      const bestLoadsValue =
        toFiniteNumber(summary?.MaxLoads ?? summary?.maxLoads) ??
        quoteMetrics?.maxLoads ??
        null;

      const bestEtdValue =
        toFiniteNumber(
          summary?.MinEstimatedTransitDays ?? summary?.minEstimatedTransitDays
        ) ??
        quoteMetrics?.minEstimatedTransitDays ??
        null;

      map.set(laneId, {
        bestUnitPrice,
        bestBidUnitValue: bestUnitPrice,
        bestBidValue,
        bestLoadsValue,
        bestEtdValue,
      });
    }

    return map;
  }, [hvLaneSummaries, hvQuotes, laneSummaryByLaneId, acceptedQuoteMetricsByLane]);

  /**
   * BidValue-based best map used ONLY by CarrierByLaneTable.
   *
   * Why this exists:
   * - CarrierByLaneTable still renders BidValue, not UnitPrice;
   * - therefore its "best bid" comparison must remain BidValue-based;
   * - this map intentionally does not expose bestUnitPrice because that metric
   *   does not belong to the carriers UI.
   */
  const carrierLaneBestMap = useMemo(() => {
    const map = new Map();

    for (const [laneId, metrics] of acceptedQuoteMetricsByLane.entries()) {
      map.set(laneId, {
        bestBidValue: metrics?.minBidValue ?? null,
        bestLoadsValue: metrics?.maxLoads ?? null,
        bestEtdValue: metrics?.minEstimatedTransitDays ?? null,
      });
    }

    return map;
  }, [acceptedQuoteMetricsByLane]);

  //endregion

  //region Spot detection

  const parseBool = (v) => {
    if (typeof v === "boolean") return v;
    if (typeof v === "number") return v !== 0;

    if (typeof v === "string") {
      const s = v.trim().toLowerCase();

      if (s === "true" || s === "1" || s === "yes") return true;
      if (s === "false" || s === "0" || s === "no" || s === "") return false;
    }

    return null;
  };

  const isSpotTender = (() => {
    const raw =
      hvTender?.IsSpotTender ??
      hvTender?.isSpotTender ??
      hvTender?.IsSpot ??
      hvTender?.isSpot;

    const parsed = parseBool(raw);
    if (parsed !== null) return parsed;

    const typeStr =
      hvTender?.TenderType ??
      hvTender?.tenderType ??
      hvTender?.Mode ??
      hvTender?.mode ??
      "";

    if (typeof typeStr !== "string") return false;

    const s = typeStr.trim().toLowerCase();
    const compact = s.replace(/[\s_-]+/g, " ");

    if (
      /\bnon\s*spot\b/.test(compact) ||
      /\bnonspot\b/.test(s.replace(/[\s_-]+/g, ""))
    ) {
      return false;
    }

    return /\bspot\b/.test(compact);
  })();

  //endregion

  //region Summary rows model

  const summaryLaneRows = useMemo(
    () =>
      buildSummaryLaneRows({
        allLanes: hvAllLanes,
        laneSummaries: hvLaneSummaries,
        isSpotTender,
      }),
    [hvAllLanes, hvLaneSummaries, isSpotTender]
  );

  //endregion

  //region Default dashboard model

  const DEFAULT_NTH = 2;

  const defaultModel = useMemo(
    () => buildFinishedTenderModel(hvQuotes, hvLanes, hvCarriers, DEFAULT_NTH),
    [hvQuotes, hvLanes, hvCarriers]
  );

  //endregion

  //region Render

  return (
    <>
      <MHeader
        element={
          <>
            <MButton
              icon={
                <i
                  className="pi pi-table iconTable"
                  style={{ color: "var(--color-green)" }}
                />
              }
              label={t("ViewDefault")}
              onClick={() => setActiveView("default")}
            />

            <MButton
              tooltip={t("Summary") || "Summary"}
              icon={
                <i
                  className="pi pi-chart-bar iconTable"
                  style={{ color: "var(--color-green-light)" }}
                />
              }
              onClick={() => setActiveView("summary")}
              label={t("ViewSummary") || "Summary"}
            />

            <MButton
              icon={
                <i
                  className="pi pi-arrow-right-arrow-left iconTable"
                  style={{ color: "var(--color-green)" }}
                />
              }
              label={t("ViewLanes")}
              onClick={() => setActiveView("lanes")}
            />

            <MButton
              icon={
                <i
                  className="pi pi-truck iconTable"
                  style={{ color: "var(--color-green)" }}
                />
              }
              label={t("ViewCarriers")}
              onClick={() => setActiveView("carriers")}
            />
          </>
        }
      />

      <div className="firstDivContainer tender-finished-container">
        {activeView === "default" && (
          <div className="tender-view-section">
            <span className="tender-view-title">{t("ViewDefault")}</span>

            <TopsByCarrierTable model={defaultModel} />

            <TopsByDestinationTable model={defaultModel} top={1} />

            <TopsByDestinationTable model={defaultModel} top={2} />

            <TopsBySourceTable model={defaultModel} top={1} />

            <TopsBySourceTable model={defaultModel} top={2} />
          </div>
        )}

        {activeView === "summary" && (
          <div className="tender-view-section tender-view-section--scroll">
            <div className="tender-view-header">
              <span className="tender-view-title">
                {t("ViewSummary") || "Summary"}
              </span>
            </div>

            <TenderSummaryTable
              tenderInfo={hvTender}
              rows={summaryLaneRows}
              isSpotTender={isSpotTender}
              onRefresh={onRefresh}
            />
          </div>
        )}

        {activeView === "lanes" && (
          <div className="tender-view-section tender-view-section--scroll">
            <div className="tender-view-header">
              <span className="tender-view-title">{t("ViewLanes")}</span>
            </div>

            {/* 
              Lanes view selector:
              - unit    => Best Unit Value
              - bid     => Bid Value
              - loads   => Loads
              - transit => ETD
            */}
            <div className="tender-metric-select-row">
              <div>
                <div className="tender-metric-select">
                  <MSelect
                    label={t("Best")}
                    options={laneMetricSelectOptions}
                    value={metricKey}
                    noNone
                    autocomplete={false}
                    onChange={(e) => {
                      const nextVal = e?.target?.value || "unit";
                      setMetricKey(nextVal);
                    }}
                  />
                </div>
              </div>
            </div>

            <ViewLaneTable
              acceptedQuotes={hvQuotes || []}
              carriers={hvCarriers || []}
              lanes={hvLanes || []}
              tenderInfo={hvTender}
              onRefresh={onRefresh}
              isFinished={true}
              isSpotTender={isSpotTender}
              currentPhaseId={null}
              /**
               * ViewLaneTable is now expected to support:
               * - unit
               * - bid
               * - loads
               * - transit
               */
              metricKey={metricKey}
              /**
               * Rich multi-metric best map.
               */
              laneBestMap={laneBestMap}
              /**
               * Future-ready props for "Loads Remaining To Satisfy".
               * These props are harmless until the table starts using them.
               */
              tenderTargetLoads={tenderTargetLoads}
              laneRemainingLoadsByLaneId={laneRemainingLoadsByLaneId}
              /**
               * Toggle infra remains wired, but with isFinished=true nothing is interactive.
               */
              acceptBusyIds={undefined}
              onSetQuoteAccepted={undefined}
            />
          </div>
        )}

        {activeView === "carriers" && (
          <div className="tender-view-section tender-view-section--scroll">
            <div className="tender-view-header">
              <span className="tender-view-title">{t("ViewCarriers")}</span>
            </div>

            {/* 
              Carriers view selector:
              - bid     => Bid Value
              - loads   => Loads
              - transit => ETD

              IMPORTANT:
              - no "unit" option here, by requirement.
            */}
            <div className="tender-metric-select-row">
              <div>
                <div className="tender-metric-select">
                  <MSelect
                    label={t("Best")}
                    options={carrierMetricSelectOptions}
                    value={metricKey === "unit" ? "bid" : metricKey}
                    noNone
                    autocomplete={false}
                    onChange={(e) => {
                      const nextVal = e?.target?.value || "bid";
                      setMetricKey(nextVal);
                    }}
                  />
                </div>
              </div>
            </div>

            <CarrierByLaneTable
              tenderInfo={hvTender}
              quotes={hvQuotes || []}
              lanes={hvLanes || []}
              carriers={hvCarriers || []}
              onRefresh={onRefresh}
              /**
               * In this table specifically:
               * - bid must be interpreted as BidValue
               * - unit is not part of the UI
               */
              metricKey={metricKey === "unit" ? "bid" : metricKey}
              /**
               * IMPORTANT:
               * - do NOT pass the richer UnitPrice-based laneBestMap here;
               * - CarrierByLaneTable must receive the BidValue-based map.
               */
              laneBestMap={carrierLaneBestMap}
            />
          </div>
        )}
      </div>
    </>
  );
}

//endregion

TenderFinishedPage.propTypes = {
  handleTabClose: PropTypes.func,
  toastMessages: PropTypes.func,
};